<template>
    <LoadingPanel :headline="title" collapsible>
        <template #context>
            <pbl-button compact @click="onDownloadAll">
                <pbl-icon slot="icon" name="download" />
                {{ t('common.downloadAll') }}
            </pbl-button>
        </template>
        <div class="report-images-measurement-section__content__list">
            <ImageCard
                v-for="image in images"
                :key="image.id"
                :image="image"
            />
        </div>
    </LoadingPanel>
</template>

<script setup lang="ts">
import { ImageDetailsDto } from '@/api/generated/HCA'
import { useDownloadImagesZipMutation } from '@/api/mutations/images/useDownloadImagesZipMutation'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import ImageCard from '@/pages/generators/images/ImageCard.vue'
import { downloadFile } from '@/utils/file-utils'
import { AxiosError } from 'axios'
import { toRefs } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    reportNumber: string,
    title: string,
    images: ImageDetailsDto[]
}>()

const { reportNumber, title, images } = toRefs(props)

const { t } = useI18n()
const toasts = usePebbleToasts()

const { mutateAsync: downloadImagesZip } = useDownloadImagesZipMutation()

const onDownloadAll = async () => {
    const filePaths = images.value
        .map(image => image.imagePath)
        .filter((path): path is string => Boolean(path))

    const analysisTitle = title.value.replace(/\s+/g, '_')
    const fileName = `${reportNumber.value}_${analysisTitle}_Images.zip`

    try {
        const blob = await downloadImagesZip({ filePaths })
        downloadFile(blob, fileName)
    } catch (error) {
        if (error instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
}
</script>

<style scoped lang="scss">
.report-images-measurement-section {
    &__content {
        &__list {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
            gap: $spacing-xs;
        }
    }
}
</style>